import { useState, useCallback, createContext, useContext, useEffect } from "react";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8787";
const TOKEN_KEY   = "fb_access_token";
const REFRESH_KEY = "fb_refresh_token";

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<string | null>;
}

interface AuthUser {
  id: string;
  email: string;
  role: string;
  tenant_id: string;
  is_platform_admin?: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken]   = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser]     = useState<AuthUser | null>(null);

  // Decode JWT payload (no verification — server handles that)
  useEffect(() => {
    if (!token) { setUser(null); return; }
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setUser({ id: payload.sub, email: payload.email, role: payload.role, tenant_id: payload.tenant_id, is_platform_admin: payload.is_platform_admin ?? false });
      // If token expires in less than 5 minutes, proactively refresh
      const expiresIn = payload.exp * 1000 - Date.now();
      if (expiresIn < 5 * 60 * 1000) {
        // Token expired or about to — attempt silent refresh
        const stored = localStorage.getItem(REFRESH_KEY);
        if (stored) {
          fetch(`${API_BASE}/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: stored }),
          }).then(r => r.ok ? r.json() : null).then(data => {
            if (data?.access_token) {
              localStorage.setItem(TOKEN_KEY, data.access_token);
              setToken(data.access_token);
            } else if (expiresIn < 0) {
              // Already expired and can't refresh — redirect to login
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(REFRESH_KEY);
              setToken(null);
              window.location.href = "/login";
            }
          }).catch(() => {});
        } else if (expiresIn < 0) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          window.location.href = "/login";
        }
      }
    } catch {
      setUser(null);
    }
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error ?? "Login failed");
    }
    const { access_token, refresh_token } = await res.json();
    localStorage.setItem(TOKEN_KEY,   access_token);
    localStorage.setItem(REFRESH_KEY, refresh_token);
    setToken(access_token);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setToken(null);
  }, []);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    const stored = localStorage.getItem(REFRESH_KEY);
    if (!stored) { logout(); window.location.href = "/login"; return null; }
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: stored }),
      });
      if (!res.ok) { logout(); window.location.href = "/login"; return null; }
      const { access_token } = await res.json();
      localStorage.setItem(TOKEN_KEY, access_token);
      setToken(access_token);
      return access_token;
    } catch {
      logout();
      window.location.href = "/login";
      return null;
    }
  }, [logout]);

  return (
    <AuthContext.Provider value={{ token, user, login, logout, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}